import React, { useEffect, useRef } from 'react';
import { Button } from '@fiverr-private/button';
import { Container } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import {
    Form,
    FormControl,
    FormElement,
    FormHeader,
    FormHelperText,
    FormLabel,
    Input,
    standardSchemaResolver,
    useForm,
    SubmitHandler,
    FieldValues,
} from '@fiverr-private/forms';
import { ArrowLeftIcon } from '@fiverr-private/visuals';
import { IdentificationFormHeader } from '../shared/IdentificationFormHeader';
import { IdentificationFormFooter } from '../shared/IdentificationFormFooter';
import { resendEmailWithCode, ResendEmailWithCodeResponse } from '../../api';
import { isAxiosError } from '../../utils/isAxiosError';
import { globalMixpanelReporter, globalBigQueryReporter, globalGrafanaReporter } from '../../events';
import { Variant } from '../../types';
import { IdentificationOnboardingFormFooter } from '../shared/IdentificationOnboardingFormFooter';
import { appTranslate } from '../../i18n';
import { createSchema, type FormSchema } from './createSchema';

interface ChangeEmailStepProps {
    email: string;
    onEmailChanged?: (email: string) => void;
    mixpanelEnrichment?: Partial<Record<string, string | number | boolean>>;
    bigQueryEnrichment?: Partial<Record<string, string | number | boolean>>;
    variant?: Variant;
    onBack?: () => void;
}

export const ChangeEmailStep = ({
    email = '',
    onEmailChanged,
    mixpanelEnrichment = {},
    bigQueryEnrichment = {},
    variant,
    onBack,
}: ChangeEmailStepProps) => {
    const isShown = useRef(false);

    const schema = createSchema();

    const methods = useForm<FormSchema>({
        resolver: standardSchemaResolver(schema),
        defaultValues: { email },
    });

    const { isSubmitting } = methods.formState;

    const onSubmit: SubmitHandler<FieldValues> = async (data) => {
        try {
            globalMixpanelReporter.send('Identification - Change Email - Submit');
            globalBigQueryReporter.send('identification.change_email.submit');
            globalGrafanaReporter.count('identification.%{platform}.change_email.submit');
            const result = await resendEmailWithCode(data.email);
            if (result.success) {
                onEmailChanged?.(data.email);
                globalMixpanelReporter.send('Identification - Change Email - Completed');
                globalBigQueryReporter.send('identification.change_email.completed');
                globalGrafanaReporter.count('identification.%{platform}.change_email.completed');
            } else {
                throw new Error(result.translatedError);
            }
        } catch (error) {
            if (isAxiosError(error)) {
                methods.setError('email', {
                    message:
                        (error.response?.data as ResendEmailWithCodeResponse)?.error ||
                        appTranslate<string>('errors.general.unspecified'),
                });
            } else if (error instanceof Error) {
                methods.setError('email', { message: error.message });
            } else {
                methods.setError('email', { message: appTranslate<string>('errors.general.unspecified') });
            }

            globalMixpanelReporter.send('Identification - Change Email - Failed');
            globalBigQueryReporter.send('identification.change_email.failed');
            globalGrafanaReporter.count('identification.%{platform}.change_email.failed');
        }
    };

    useEffect(() => {
        globalMixpanelReporter.enrichContext(mixpanelEnrichment);
        globalBigQueryReporter.enrichContext(bigQueryEnrichment);
        if (!isShown.current) {
            globalMixpanelReporter.send('Identification - Change Email - Show');
            globalBigQueryReporter.send('identification.change_email.show');
            globalGrafanaReporter.count('identification.%{platform}.change_email.show');
            isShown.current = true;
        }
    }, [mixpanelEnrichment, bigQueryEnrichment]);

    const isOnboarding = variant === 'onboarding';
    const submitButton = (
        <Button marginLeft={isOnboarding ? 'auto' : undefined} loading={isSubmitting} type="submit">
            {appTranslate('general.submit')}
        </Button>
    );

    return (
        <Container display="flex" direction="column" height="100%" minHeight={0}>
            <Form onSubmit={onSubmit} methods={methods} flex="1" minHeight={0}>
                <Container minHeight={0} overflowY="auto" maxWidth={isOnboarding ? '550px' : undefined}>
                    <IdentificationFormHeader
                        variant={variant}
                        title={appTranslate<string>(`change_email.title`)}
                        subTitle={appTranslate<string>(`change_email.subtitle`, {
                            params: { email },
                            templates: {
                                b: (text?: string) => (
                                    <Typography color="grey_1100" fontWeight="semibold" as="span">
                                        {text}
                                    </Typography>
                                ),
                            },
                        })}
                    />
                    <FormControl name="email">
                        <FormHeader>
                            <FormLabel>
                                <Typography fontWeight="semibold" marginTop="6" color="grey_1100">
                                    {appTranslate('fields.email.label')}
                                </Typography>
                            </FormLabel>
                        </FormHeader>
                        <FormElement>
                            <Input placeholder="name@email.com" autoFocus />
                        </FormElement>
                        <FormHelperText />
                    </FormControl>
                </Container>
                {isOnboarding ? (
                    <IdentificationOnboardingFormFooter>
                        {onBack && (
                            <Button
                                type="button"
                                hidden={{ default: true, sm: false }}
                                size="md"
                                variant="ghost"
                                onClick={onBack}
                            >
                                <ArrowLeftIcon size="sm" />
                                <Container as="span">{appTranslate('general.back')}</Container>
                            </Button>
                        )}

                        {submitButton}
                    </IdentificationOnboardingFormFooter>
                ) : (
                    <IdentificationFormFooter justifyContent="flexEnd">{submitButton}</IdentificationFormFooter>
                )}
            </Form>
        </Container>
    );
};

export default ChangeEmailStep;
